import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../shell/TenantContext';
import { useAuthStore } from '../store/authStore';
import EnrollModal from '../modules/academy/EnrollModal';
import { academyPath } from '../modules/academy/academyRoute';
import LegalLinks from '../components/LegalLinks';
import { parkReturnPath } from '../lib/authReturn';
import MaicBrand from '../components/MaicBrand';
import { usePublicInterface, v1OrClass } from './publicUi';
import './public.css';

const AUTH_API = import.meta.env.VITE_AUTH_SERVICE_URL || 'http://localhost:8011';
const SSO_ENABLED = !!import.meta.env.VITE_AUTH_SERVICE_URL;

// ── maic icon — muted for login screen ───────────────────────────────────

export const MaicLoginIcon: React.FC = () => (
  <svg width="52" height="52" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="18" x2="9"  y2="9"  stroke="#4A5568" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="26" y1="18" x2="35" y2="9"  stroke="#4A5568" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="18" y1="26" x2="9"  y2="35" stroke="#4A5568" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="26" y1="26" x2="35" y2="35" stroke="#4A5568" strokeWidth="2.5" strokeLinecap="round" />
    <line x1="26" y1="22" x2="31" y2="22" stroke="#4A5568" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="8"  cy="8"  r="5" fill="#4A5568" />
    <circle cx="36" cy="8"  r="5" fill="#4A5568" />
    <circle cx="8"  cy="36" r="5" fill="#4A5568" />
    <circle cx="36" cy="36" r="5" fill="#4A5568" />
    <circle cx="33" cy="22" r="3" fill="#4A5568" />
    <rect x="17" y="17" width="10" height="10" rx="2" fill="#4A5568" />
  </svg>
);

// ── User icon for input ───────────────────────────────────────────────────

// `v2` strokes with the text color of its field instead of the fixed slate.
const UserIcon: React.FC<{ v2?: boolean }> = ({ v2 }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={v2 ? 'currentColor' : '#5B6B85'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const LockIcon: React.FC<{ v2?: boolean }> = ({ v2 }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={v2 ? 'currentColor' : '#5B6B85'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ── LoginPage ─────────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [step, setStep] = useState<'email' | 'password' | 'totp'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  // New interface only when this browser forces it (?ui=v2): a page without
  // a session has no tenant flag to read (pages/publicUi.ts). With it, the
  // same elements, inputs and handlers take classes from public.css (theme
  // tokens, 360 px card, 30 px controls) instead of the inline colors.
  const v2 = usePublicInterface();
  const sx = (v1: React.CSSProperties, className: string) => v1OrClass(v2, v1, className);

  const W = 210; // form element width

  const inputStyle = (focused: boolean): React.CSSProperties => ({
    width: W,
    height: 36,
    backgroundColor: '#1F2D3D',
    border: `1px solid ${focused ? '#4F6BC6' : '#2D3F55'}`,
    borderRadius: 3,
    color: '#C5CDD8',
    fontSize: 13,
    padding: '0 10px 0 32px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    caretColor: '#C5CDD8',
  });

  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused] = useState(false);

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('auth.enterEmail', 'Enter your email address.')); return; }
    setError('');
    setStep('password');
    setTimeout(() => {
      (document.getElementById('maic-pass') as HTMLInputElement)?.focus();
    }, 50);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) { setError(t('auth.enterPassword', 'Enter your password.')); return; }
    if (step === 'totp' && !totp.trim()) { setError(t('auth.enterTotp', 'Enter your 6-digit code.')); return; }
    setLoading(true);
    setError('');
    const result = await login(email.trim(), password, step === 'totp' ? totp.trim() : undefined);
    if (!result.success) {
      // Backend answers 401 "MFA required — provide totp_code…" when the
      // account has MFA enabled and no code was sent: switch to the TOTP step.
      if ((result.error || '').toLowerCase().includes('mfa required')) {
        setStep('totp');
        setError('');
        setLoading(false);
        setTimeout(() => {
          (document.getElementById('maic-totp') as HTMLInputElement)?.focus();
        }, 50);
        return;
      }
      // The server answers one sentence for a wrong password, an unknown email
      // and a locked account (on purpose: telling them apart tells a stranger
      // which addresses have accounts). So the page says what can be done in
      // each case instead of naming which one it is.
      const detail = result.error || '';
      setError(
        !detail || detail === 'Invalid credentials' || detail === 'Login failed'
          ? t('auth.loginFailedHint', 'Correo o contraseña incorrectos. Si fallaste varias veces, la cuenta se bloquea unos minutos. Si te dieron una contraseña temporal, escríbela tal cual.')
          : detail,
      );
      setLoading(false);
    }
  };

  return (
    <div {...sx({
      minHeight: '100vh',
      backgroundColor: '#1B2333',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Anchors the policy links pinned to the bottom of the screen.
      position: 'relative',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif',
      backgroundImage: 'radial-gradient(ellipse at 50% 40%, #1E2A3D 0%, #151D2B 100%)',
    }, 'pub-page')}>
      <div {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }, 'pub-card')}>

        {/* Logo */}
        {v2 ? (
          <>
            <MaicBrand className="pub-brand" />
            <h1 className="pub-title">{t('auth.signIn', 'Sign in')}</h1>
          </>
        ) : (
          <div style={{ marginBottom: 20 }}>
            <MaicLoginIcon />
          </div>
        )}

        {/* Email step */}
        {step === 'email' && (
          <form onSubmit={handleNext} {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }, 'pub-form')}>
            {/* Email input */}
            <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
              <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
                <UserIcon v2={v2} />
              </div>
              <input
                id="maic-email"
                type="text"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder={t('auth.emailPlaceholder', 'user@domain.com')}
                autoFocus
                autoComplete="email"
                {...sx(inputStyle(emailFocused), 'pub-input')}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
              />
            </div>

            {/* Remember me */}
            <label {...sx({
              display: 'flex', alignItems: 'center', gap: 8,
              width: W, cursor: 'pointer',
              fontSize: 12, color: '#8A9BAE',
            }, 'pub-check')}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                {...sx({ accentColor: '#4F6BC6', width: 13, height: 13, margin: 0 }, 'pub-check__box')}
              />
              {t('auth.rememberMe', 'Remember me')}
            </label>

            {error && (
              <div {...sx({ width: W, fontSize: 11, color: '#F87171', textAlign: 'center' }, 'pub-error')} role={v2 ? 'alert' : undefined}>
                {error}
              </div>
            )}

            {/* Next button */}
            <button
              type="submit"
              {...sx({
                width: W, height: 34,
                backgroundColor: '#4054A8',
                border: 'none', borderRadius: 3,
                color: '#BCC8E0', fontSize: 13, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit',
                letterSpacing: '0.02em',
                transition: 'background-color 120ms',
              }, 'ds-btn ds-btn--primary pub-btn')}
              onMouseEnter={(e) => { if (!v2) e.currentTarget.style.backgroundColor = '#4A61C0'; }}
              onMouseLeave={(e) => { if (!v2) e.currentTarget.style.backgroundColor = '#4054A8'; }}
            >
              {/* v2 says it in the screen's language (the key auth.next has no
                  Spanish text, and adding one would change v1). */}
              {v2 ? t('auth.continue', 'Continuar') : t('auth.next', 'Next')}
            </button>
          </form>
        )}

        {/* SSO buttons — shown only in email step when auth service is configured */}
        {step === 'email' && SSO_ENABLED && (
          <div {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginTop: 6 }, 'pub-sso')}>
            <div {...sx({ display: 'flex', alignItems: 'center', gap: 8, width: W, marginBottom: 2 }, 'pub-divider')}>
              <div {...sx({ flex: 1, height: 1, backgroundColor: '#2D3F55' }, 'pub-divider__line')} />
              <span {...sx({ fontSize: 10, color: '#4A5568' }, 'pub-divider__text')}>{t('auth.orSignInWith', 'or sign in with')}</span>
              <div {...sx({ flex: 1, height: 1, backgroundColor: '#2D3F55' }, 'pub-divider__line')} />
            </div>
            {(['google', 'okta', 'azure'] as const).map(provider => (
              <button
                key={provider}
                type="button"
                onClick={() => {
                  // SSO leaves this page: park the path to come back to it (GSL-4).
                  parkReturnPath();
                  window.location.href = `${AUTH_API}/auth/oidc/${provider}`;
                }}
                {...sx({
                  width: W, height: 32,
                  backgroundColor: '#1F2D3D', border: '1px solid #2D3F55', borderRadius: 3,
                  color: '#8A9BAE', fontSize: 12, fontFamily: 'inherit',
                  cursor: 'pointer', textTransform: 'capitalize', letterSpacing: '0.02em',
                  transition: 'border-color 120ms, color 120ms',
                }, 'ds-btn ds-btn--secondary pub-btn')}
                onMouseEnter={(e) => {
                  if (v2) return;
                  e.currentTarget.style.borderColor = '#4F6BC6';
                  e.currentTarget.style.color = '#BCC8E0';
                }}
                onMouseLeave={(e) => {
                  if (v2) return;
                  e.currentTarget.style.borderColor = '#2D3F55';
                  e.currentTarget.style.color = '#8A9BAE';
                }}
              >
                {provider === 'azure' ? 'Microsoft' : provider.charAt(0).toUpperCase() + provider.slice(1)}
              </button>
            ))}
          </div>
        )}

        {/* TOTP step — account has MFA enabled */}
        {step === 'totp' && (
          <form onSubmit={handleSignIn} {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }, 'pub-form')}>
            <div {...sx({
              width: W, fontSize: 12, color: '#8A9BAE', textAlign: 'center',
              marginBottom: 2,
            }, 'pub-who')}>
              {email}
              <button
                type="button"
                onClick={() => { setStep('email'); setError(''); setPassword(''); setTotp(''); }}
                {...sx({
                  marginLeft: 8, fontSize: 11, color: '#4F6BC6',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'inherit',
                }, 'pub-link')}
              >
                {t('auth.change', 'change')}
              </button>
            </div>

            <div {...sx({ width: W, fontSize: 11, color: '#8A9BAE', textAlign: 'center' }, 'pub-hint')}>
              {t('auth.totpInstructions', 'Enter the 6-digit code from your authenticator app')}
            </div>

            <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
              <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
                <LockIcon v2={v2} />
              </div>
              <input
                id="maic-totp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={totp}
                onChange={(e) => { setTotp(e.target.value.replace(/\D/g, '')); setError(''); }}
                placeholder="000000"
                autoComplete="one-time-code"
                {...sx({ ...inputStyle(passFocused), letterSpacing: '0.3em', textAlign: 'center', padding: '0 10px' }, 'pub-input pub-input--code')}
                onFocus={() => setPassFocused(true)}
                onBlur={() => setPassFocused(false)}
              />
            </div>

            {error && (
              <div {...sx({ width: W, fontSize: 11, color: '#F87171', textAlign: 'center' }, 'pub-error')} role={v2 ? 'alert' : undefined}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              {...sx({
                width: W, height: 34,
                backgroundColor: loading ? '#2E3E6A' : '#4054A8',
                border: 'none', borderRadius: 3,
                color: '#BCC8E0', fontSize: 13, fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.02em',
                transition: 'background-color 120ms',
              }, 'ds-btn ds-btn--primary pub-btn')}
              onMouseEnter={(e) => { if (!loading && !v2) e.currentTarget.style.backgroundColor = '#4A61C0'; }}
              onMouseLeave={(e) => { if (!loading && !v2) e.currentTarget.style.backgroundColor = '#4054A8'; }}
            >
              {loading ? t('auth.verifying', 'Verifying...') : t('auth.verify', 'Verify')}
            </button>
          </form>
        )}

        {/* Password step */}
        {step === 'password' && (
          <form onSubmit={handleSignIn} {...sx({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }, 'pub-form')}>
            {/* Email display (readonly) */}
            <div {...sx({
              width: W, fontSize: 12, color: '#8A9BAE', textAlign: 'center',
              marginBottom: 2,
            }, 'pub-who')}>
              {email}
              <button
                type="button"
                onClick={() => { setStep('email'); setError(''); setPassword(''); }}
                {...sx({
                  marginLeft: 8, fontSize: 11, color: '#4F6BC6',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'inherit',
                }, 'pub-link')}
              >
                {t('auth.change', 'change')}
              </button>
            </div>

            {/* Password input */}
            <div {...sx({ position: 'relative', width: W }, 'pub-field')}>
              <div {...sx({ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0, pointerEvents: 'none' }, 'pub-field__icon')}>
                <LockIcon v2={v2} />
              </div>
              <input
                id="maic-pass"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder={t('auth.passwordPlaceholder', 'Password')}
                autoComplete="current-password"
                {...sx(inputStyle(passFocused), 'pub-input')}
                onFocus={() => setPassFocused(true)}
                onBlur={() => setPassFocused(false)}
              />
            </div>

            {error && (
              <div {...sx({ width: W, fontSize: 11, color: '#F87171', textAlign: 'center' }, 'pub-error')} role={v2 ? 'alert' : undefined}>
                {error}
              </div>
            )}

            {/* Sign in button */}
            <button
              type="submit"
              disabled={loading}
              {...sx({
                width: W, height: 34,
                backgroundColor: loading ? '#2E3E6A' : '#4054A8',
                border: 'none', borderRadius: 3,
                color: '#BCC8E0', fontSize: 13, fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.02em',
                transition: 'background-color 120ms',
              }, 'ds-btn ds-btn--primary pub-btn')}
              onMouseEnter={(e) => { if (!loading && !v2) e.currentTarget.style.backgroundColor = '#4A61C0'; }}
              onMouseLeave={(e) => { if (!loading && !v2) e.currentTarget.style.backgroundColor = '#4054A8'; }}
            >
              {loading ? t('auth.signingIn', 'Signing in...') : t('auth.signIn', 'Sign in')}
            </button>
          </form>
        )}

        {/* PAI Academy self-serve enrollment */}
        {step === 'email' && (
          <button
            onClick={() => setShowEnroll(true)}
            data-academy="enroll-open"
            {...sx({
              marginTop: 22, border: 'none', backgroundColor: 'transparent',
              color: '#7E8CA3', fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.02em',
              textDecoration: 'underline', textUnderlineOffset: 3,
            }, 'pub-alt pub-alt--first')}
            onMouseEnter={(e) => { if (!v2) e.currentTarget.style.color = '#A78BFA'; }}
            onMouseLeave={(e) => { if (!v2) e.currentTarget.style.color = '#7E8CA3'; }}
          >
            {t('auth.academyEnrollLink', 'Certifícate con PAI Academy — inscríbete aquí')}
          </button>
        )}
        {/* Public catalog (GSL-3): every certification and its syllabus,
            browsable before enrolling. A plain link: it renders outside the
            auth gate. */}
        {step === 'email' && (
          <a
            href={academyPath({ view: 'catalog' })}
            data-academy="catalog-link"
            {...sx({
              marginTop: 8, color: 'var(--color-nav-text)', fontSize: 11.5,
              letterSpacing: '0.02em', textDecoration: 'none',
            }, 'pub-alt')}
          >
            {t('auth.academyCatalogLink', 'Ver el catálogo de cursos')}
          </a>
        )}
      </div>

      {/* Public policies. Reachable without an account by design — someone
          who cannot sign in still has to be able to read the policy and file
          a data-deletion request. */}
      <LegalLinks
        tone="dark"
        v2={v2}
        {...(v2 ? { labels: { privacy: t('legal.privacy', 'Privacy Policy'), terms: t('legal.terms', 'Terms of Service'), deletion: t('legal.deletion', 'Data Deletion') } } : {})}
        style={{ position: 'absolute', bottom: 22, left: 0, right: 0 }}
      />

      {showEnroll && <EnrollModal onClose={() => setShowEnroll(false)} />}
    </div>
  );
};

export default LoginPage;
